import _ from "lodash";

const getCycleIdToSetIdMapping = ({ cycles }) => {
  //returns mapping of cycle id to set id
  return _.reduce(
    cycles,
    (acc, cycle) => {
      const cycleId = _.get(cycle, "id", "");
      const setId = _.get(cycle, "set.id", "");
      acc[cycleId] = setId;
      return acc;
    },
    {}
  );
};

export const getCycleIdToSetIdMappingMemoized = _.memoize(
  getCycleIdToSetIdMapping,
  ({ cycles }) => {
    return JSON.stringify({
      cycles: _.map(cycles, cycle => {
        return {
          id: cycle?.id,
          set: _.pick(cycle?.set, ["id"]),
        };
      }),
    });
  }
);

const getTaggedPracticesForCycle = ({ taggedPractices, cycleId }) => {
  return _.find(
    taggedPractices,
    ({ evaluationCycle }) => evaluationCycle.id === cycleId
  );
};

export const getPracticeWrappersForCycle = ({ taggedPractices, cycleId }) => {
  const taggedPracticesForCycle = getTaggedPracticesForCycle({
    taggedPractices,
    cycleId,
  });

  return _.get(taggedPracticesForCycle, "taggedPractice", []);
};

const getDisabledPracticeWrappers = ({
  practiceWrappers,
  canManageEvaluationCycles,
  userId,
}) => {
  if (canManageEvaluationCycles) return [];
  return _.filter(practiceWrappers, ({ taggedBy, evidenceStatus }) => {
    return taggedBy.id !== userId || evidenceStatus === "APPROVED";
  });
};

const getAddedPractices = ({ selectedPractices, taggedPractices }) => {
  return _.filter(
    selectedPractices,
    practiceId => !_.includes(taggedPractices, practiceId)
  );
};

export const getAddedPracticesMemoized = _.memoize(
  getAddedPractices,
  ({ selectedPractices, taggedPractices }) => {
    return JSON.stringify({ selectedPractices, taggedPractices });
  }
);

const getRemovedPractices = ({ selectedPractices, taggedPractices }) => {
  return _.filter(
    taggedPractices,
    practiceId => !_.includes(selectedPractices, practiceId)
  );
};

export const getRemovedPracticesMemoized = _.memoize(
  getRemovedPractices,
  ({ selectedPractices, taggedPractices }) => {
    return JSON.stringify({ selectedPractices, taggedPractices });
  }
);

const getCycleIdToSelectedPracticesCountMapping = ({
  cycles,
  taggedPractices,
}) => {
  return _.reduce(
    cycles,
    (result, { id }) => {
      const practiceWrappers = getPracticeWrappersForCycle({
        taggedPractices,
        cycleId: id,
      });

      result[id] = _.size(practiceWrappers);

      return result;
    },
    {}
  );
};

export const getCycleIdToSelectedPracticesCountMappingMemoized = _.memoize(
  getCycleIdToSelectedPracticesCountMapping,
  ({ cycles, taggedPractices }) => {
    return JSON.stringify({
      cycles: _.map(cycles, cycle => _.pick(cycle, ["id"])),
      taggedPractices: _.map(taggedPractices, practice => ({
        evaluationCycle: { id: practice?.evaluationCycle?.id },
        taggedPractice: _.map(practice?.taggedPractice, ({ practice }) => {
          return {
            practice: _.pick(practice, ["id"]),
          };
        }),
      })),
    });
  }
);

const getDisabledPracticesForCycle = ({
  practiceWrappers,
  userId,
  canManageEvaluationCycles,
}) => {
  const disabledPracticeWrappers = getDisabledPracticeWrappers({
    practiceWrappers,
    canManageEvaluationCycles,
    userId,
  });

  return _.map(disabledPracticeWrappers, ({ practice }) => practice.id);
};

export const getDisabledPracticesForCycleMemoized = _.memoize(
  getDisabledPracticesForCycle,
  ({ userId, canManageEvaluationCycles, practiceWrappers }) => {
    return JSON.stringify({
      userId,
      canManageEvaluationCycles,
      practiceWrappers: _.map(
        practiceWrappers,
        ({ practice, taggedBy, evidenceStatus }) => {
          return {
            practice: _.pick(practice, ["id"]),
            taggedBy: _.pick(taggedBy, ["id"]),
            evidenceStatus,
          };
        }
      ),
    });
  }
);

const getPracticesForCycle = ({
  practiceWrappers,
  userId,
  canManageEvaluationCycles,
}) => {
  const disabledPracticesForCycle = getDisabledPracticesForCycleMemoized({
    practiceWrappers,
    userId,
    canManageEvaluationCycles,
  });

  const taggedPracticesForCycle = _.map(
    practiceWrappers,
    ({ practice }) => practice.id
  );

  return {
    taggedPracticesForCycle: _.difference(
      taggedPracticesForCycle,
      disabledPracticesForCycle
    ),
    disabledPracticesForCycle,
  };
};

export const getPracticesForCycleMemoized = _.memoize(
  getPracticesForCycle,
  ({ userId, canManageEvaluationCycles, practiceWrappers }) => {
    return JSON.stringify({
      userId,
      canManageEvaluationCycles,
      practiceWrappers: _.map(
        practiceWrappers,
        ({ practice, taggedBy, evidenceStatus }) => {
          return {
            practice: _.pick(practice, ["id"]),
            taggedBy: _.pick(taggedBy, ["id"]),
            evidenceStatus,
          };
        }
      ),
    });
  }
);

export const getApprovedPracticesForCycle = ({ practiceWrappers }) => {
  const approvedPracticeWrappers = _.filter(
    practiceWrappers,
    ({ evidenceStatus }) => evidenceStatus === "APPROVED"
  );
  return _.map(approvedPracticeWrappers, ({ practice: { id } }) => id);
};
